import React from 'react';
import {
  Button,
  Form,
  FormGroup,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  ImageCircle,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  Paragraph,
  Radio,
  Textarea,
  Layout,
  LayoutItem,
} from '@axiom/ui';
import { z } from 'zod';
import type { AfcSubmissionCandidate } from '@axiom/validation';
import { ClientRequestRejectionLossCodes } from '@axiom/validation';
import { CandidateImageConst, ClientRequestConst } from '@axiom/const';
import { ZodArrayToEnum } from '@axiom/types';

import { CandidateUtil } from '../../utils/candidate-util';

const EngagementPassModalFormSchema = z.object({
  rejectionLossCode: z.enum(ZodArrayToEnum(ClientRequestRejectionLossCodes)),
  notes: z.string().max(1000).nullable(),
  LOSS_OPTIONS: z.string().optional(),
});
export type EngagementPassModalForm = z.infer<
  typeof EngagementPassModalFormSchema
>;

type EngagementPassModalProps = {
  candidate: AfcSubmissionCandidate;
  closeModal: () => void;
  submitModal: (formData: EngagementPassModalForm) => Promise<void>;
};

const { PossibleImageSizes } = CandidateImageConst;
const { AfcLossReasonLabels } = ClientRequestConst;
export const EngagementPassModal = ({
  candidate,
  closeModal,
  submitModal,
}: EngagementPassModalProps) => {
  return (
    <Form
      name="PASS_FORM"
      schema={EngagementPassModalFormSchema}
      onSubmit={async data => {
        // @ts-expect-error Need type fix wtih APCORE-2687
        await submitModal(data);
      }}
      initialValues={{
        notes: null,
      }}
    >
      {({ fireSubmit }) => (
        <Modal name="PASS_MODAL">
          <ModalHeader name="PASS_HEADER" onClose={closeModal}>
            <Layout position="middle" name="PASS_HEADER_LAYOUT">
              <LayoutItem rightGutter="16px">
                <ImageCircle
                  size="small"
                  name="HEADER_IMAGE"
                  imageName={candidate.calculatedDisplayName}
                  src={CandidateUtil.getProfileImageUri(
                    candidate,
                    PossibleImageSizes.W_56
                  )}
                />
              </LayoutItem>
              <LayoutItem>
                <Layout wrap>
                  Passing on {candidate.calculatedDisplayName}?
                </Layout>
              </LayoutItem>
            </Layout>
          </ModalHeader>
          <ModalSection name="PASS_BODY">
            <Grid>
              <GridRow>
                <GridColumn>
                  <Gutter bottom="48px">
                    <Paragraph name="BODY_COPY">
                      No problem. We'll remove {candidate.calculatedDisplayName}{' '}
                      from your matches so Axiom can consider them for other
                      opportunities and you can focus on talent you're more
                      interested in.
                    </Paragraph>
                  </Gutter>
                </GridColumn>
              </GridRow>
              <GridRow>
                <GridColumn>
                  <Gutter bottom="16px">
                    <FormGroup
                      name="LOSS_OPTIONS"
                      label="Why are you passing on this person?"
                    >
                      {Object.entries(AfcLossReasonLabels).map(
                        ([key, value]) => (
                          <Gutter key={key} bottom="8px">
                            <Radio
                              name="rejectionLossCode"
                              option={key}
                              displayValue={value}
                            />
                          </Gutter>
                        )
                      )}
                    </FormGroup>
                  </Gutter>
                </GridColumn>
              </GridRow>
              <GridRow>
                <GridColumn>
                  <Textarea
                    name="notes"
                    label="Anything else to add that may help to improve future matches?"
                  />
                </GridColumn>
              </GridRow>
            </Grid>
          </ModalSection>
          <ModalFooter name="PASS_FOOTER">
            <Button name="CANCEL" variation="outline" onClick={closeModal}>
              Don't Pass
            </Button>
            <Button name="PASS_SUBMIT" onClick={fireSubmit}>
              Yes, Pass
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </Form>
  );
};
